import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Navigate } from "react-router-dom"

import UserGroup from "~/components/UserGroup/UserGroup"
import Sidebar from "~/components/Sidebar/Sidebar"
import Timer from "~/components/Timer/Timer"
import Button from "~/components/Button/Button"
import TimerPicker from "~/components/TimerPicker/TimerPicker"
import PageContent from "~/components/PageContent/PageContent"
import PageWithSidebar from "~/components/PageWithSidebar/PageWithSidebar"
import NotificationModal from "~/components/NotificationModal/NotificationModal"

import { useParams } from "react-router-dom"

import "./Space.scss"
import axios from "axios"
import { postEvent } from "~/utils/EventManager"
import {
  getCycle,
  getUser,
  setUser,
  switchCycle,
  getCurrentSpace,
} from "~/store"
import { setSpaceName } from "../../store"
import SpaceActions from "../../components/SpaceActions/SpaceActions"
import { setCycleFavicon } from "../../utils/Favicon"
import { playAlarm } from "~/utils/AlarmUtils"
import { Space } from "~/types"
import { showTimerEndedNotification } from "~/utils/NotificationUtils"
import { useParticipantNotifications } from "~/hooks/useParticipantNotifications"
import { useDeclareActiveSpacePage } from "~/providers/ActiveSpacePageProvider"

function Space() {
  const user = useSelector(getUser)
  const dispatch = useDispatch()
  const cycle = useSelector(getCycle)
  const { spaceName } = useParams()
  const currentSpace = useSelector(getCurrentSpace)
  const timer = currentSpace?.current_timer
  const [redirect, setRedirect] = useState(false)
  const [aTimerHasEnded, setATimerHasEnded] = useState(false)
  const timerIsRunning = Boolean(currentSpace?.current_timer)

  useParticipantNotifications({ user, space: currentSpace })
  useDeclareActiveSpacePage({ channelSpaceName: spaceName ?? null })

  useEffect(() => {
    dispatch(setSpaceName(spaceName))
  }, [])

  useEffect(() => {
    let timerTitleReminderOn = false
    const interval = setInterval(() => {
      if (currentSpace && aTimerHasEnded) {
        if (!currentSpace.current_timer) {
          document.title = timerTitleReminderOn
            ? "Pomodoro timer ended"
            : "Pomodoro"
          timerTitleReminderOn = !timerTitleReminderOn
        }
      }
    }, 3000)
    return () => {
      document.title = "Pomodoro"
      clearInterval(interval)
    }
  }, [currentSpace, aTimerHasEnded])

  useEffect(() => {
    if (timerIsRunning) return

    setCycleFavicon(cycle)

    return () => setCycleFavicon("work")
  }, [cycle, timerIsRunning])

  const leaveCurrentSpace = async () => {
    const response = await axios.put(`/api/users/${user?.id}`, {
      user: { space_id: null },
    })

    postEvent({ spaceName }, "userLeft")
    const userData = response.data["data"]["attributes"]
    dispatch(setUser(userData))
    dispatch(setSpaceName(null))
    setRedirect(true)
  }

  const handleCreate = (minutes: number) => {
    const params = {
      timer: {
        duration: minutes * 60,
        break: cycle === "break",
      },
    }

    axios
      .post(`/api/spaces/${spaceName}/timers`, params)
      .then(() => {
        postEvent({ spaceName }, "timerCreated")
      })
      .catch((error) => {
        console.error(error)
      })
  }

  const handleCancel = () => {
    axios.delete(`/api/spaces/${spaceName}/timers/current`).then(() => {
      postEvent({ spaceName }, "timerCancelled")
    })
  }

  if (redirect) {
    return <Navigate to={"/spaces"} />
  }

  if (!currentSpace || currentSpace.name !== spaceName) {
    return <div />
  }

  const playAlertSound = () => {
    if (user?.alert && user.alert_volume) {
      playAlarm(user.alert_volume)
    }
  }

  const handleEnded = () => {
    if (timer) postEvent({ spaceName }, "timerEnded", { timer_id: timer.id })

    if (user?.space_name == spaceName) {
      setATimerHasEnded(true)
      playAlertSound()
      showTimerEndedNotification(timer)
    }
  }

  return (
    <PageWithSidebar>
      <PageContent
        className={`Space__page-content--${
          cycle === "break" ? "break" : "work"
        }`}
      >
        <div className="Space">
          {timerIsRunning && currentSpace.current_timer ? (
            <Timer
              timer={currentSpace.current_timer}
              onCancel={handleCancel}
              onEnded={handleEnded}
            />
          ) : (
            <div className="Space__mode">
              <TimerPicker
                cycle={cycle}
                onCreate={(minutes) => handleCreate(Number(minutes))}
              />
              <Button
                onClick={() => dispatch(switchCycle())}
                style={"outline"}
                className={`Space__cycle--${cycle}`}
                text={cycle == "work" ? "Take a break" : "Back to work"}
              />
            </div>
          )}
        </div>
        <div className="Space__actions">
          <UserGroup
            hideTitle={false}
            guests={currentSpace.guests}
            users={currentSpace.users}
          />
          <SpaceActions space={currentSpace} />
        </div>
      </PageContent>
      <Sidebar
        style="secondary"
        onSpacePage={true}
        onLeaveButtonClick={leaveCurrentSpace}
      />
      <NotificationModal cycle={cycle} />
    </PageWithSidebar>
  )
}

export default Space
